// Item picture thumbnail (2026-09-22). 36px in tables and the item picker, 56px where there is room.
// A missing picture is a dashed placeholder, never a button: pictures are set in the composer only.
import React, { useState } from 'react';
import { Tooltip } from 'antd';
import PictureLightbox from './PictureLightbox';

export type ItemThumbSize = 'sm' | 'lg';

const PX: Record<ItemThumbSize, number> = { sm: 36, lg: 56 };

export interface ItemThumbProps {
  /** Presigned GET (catalog) or an object URL (staged composer batch). Null = no picture. */
  url?: string | null;
  size?: ItemThumbSize;
  /** Catalog item, for the lightbox's "Open item card" link. */
  itemId?: number | null;
  code?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  /** File size in bytes, shown in the lightbox meta. */
  bytes?: number | null;
  date?: string | null;
  /** Click opens the lightbox. Off inside the item picker, where the click belongs to the option. */
  clickable?: boolean;
  /** Placeholder glyph. "+" on a gap, "—" on the sync review's "removed" side. */
  emptyGlyph?: React.ReactNode;
  emptyTitle?: string;
  /** Presigned URLs expire after 15 minutes; the hook refetches once for this id. */
  onError?: () => void;
  style?: React.CSSProperties;
}

const ItemThumb: React.FC<ItemThumbProps> = ({
  url,
  size = 'sm',
  itemId,
  code,
  description,
  descriptionAr,
  bytes,
  date,
  clickable = true,
  emptyGlyph = '+',
  emptyTitle = 'Add in the composer',
  onError,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const px = PX[size];
  const base: React.CSSProperties = {
    width: px,
    height: px,
    flex: 'none',
    borderRadius: 4,
    display: 'inline-block',
    verticalAlign: 'middle',
    ...style,
  };

  if (!url) {
    return (
      <Tooltip title={emptyTitle}>
        <span
          aria-label="No picture"
          style={{
            ...base,
            border: '1px dashed #c9cede',
            background: '#f3f4f7',
            color: '#a2a8b5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size === 'lg' ? 20 : 16,
            lineHeight: 1,
          }}
        >
          {emptyGlyph}
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <img
        src={url}
        alt={description || code || 'Item picture'}
        loading="lazy"
        onError={onError}
        onClick={
          clickable
            ? (e) => {
                // Table rows navigate on click; the thumbnail must not take the reader off the list.
                e.stopPropagation();
                e.preventDefault();
                setOpen(true);
              }
            : undefined
        }
        style={{
          ...base,
          objectFit: 'cover',
          border: '1px solid #d3d7e0',
          background: '#f3f4f7',
          cursor: clickable ? 'zoom-in' : 'default',
        }}
      />
      {clickable && open && (
        <PictureLightbox
          open={open}
          onClose={() => setOpen(false)}
          url={url}
          itemId={itemId}
          code={code}
          description={description}
          descriptionAr={descriptionAr}
          size={bytes}
          date={date}
        />
      )}
    </>
  );
};

export default ItemThumb;
